import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { resultadoCarrera, reintentarCarrera } from "@/lib/carrera.functions";
import { euro } from "@/lib/carrera.shared";
import { enviarFormularioRedsys } from "@/lib/redsys-form";
export const Route = createFileRoute("/san-silvestre/resultado")({
  validateSearch: (input) => ({ token: z.string().uuid().catch("").parse(input.token) }),
  head: () => ({
    meta: [
      { title: "Estado de tu inscripción · San Silvestre" },
      {
        name: "description",
        content:
          "Consulta el estado del pago de tu inscripción a la San Silvestre de la Sierra Norte.",
      },
      { property: "og:title", content: "Tu inscripción · San Silvestre" },
      { property: "og:description", content: "Estado de la inscripción y pago." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Resultado,
});
function Resultado() {
  const { token } = Route.useSearch();
  const read = useServerFn(resultadoCarrera);
  const retry = useServerFn(reintentarCarrera);
  const [state, setState] = useState<Awaited<ReturnType<typeof resultadoCarrera>> | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) {
        setError("Enlace de inscripción inválido");
        return;
      }
      try {
        const data = await read({ data: { token } });
        if (active) setState(data);
      } catch {
        if (active) setError("No se pudo consultar la inscripción");
      }
    };
    void load();
    const timer = setInterval(load, 6000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [token, read]);
  async function pay() {
    setBusy(true);
    try {
      const r = await retry({ data: { token } });
      enviarFormularioRedsys(r.form);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo reintentar");
      setBusy(false);
    }
  }
  return (
    <main className="mx-auto max-w-2xl px-4 py-20">
      <p className="text-sm text-accent">SAN SILVESTRE DE LA SIERRA NORTE</p>
      <h1 className="mt-4 font-serif text-4xl">
        {state?.estado === "pagada"
          ? "¡Inscripción confirmada!"
          : state?.pago === "fallido"
            ? "Pago no completado"
            : "Comprobando tu inscripción"}
      </h1>
      {state && (
        <>
          <p className="mt-6">
            Referencia: <strong>{state.referencia}</strong> · {euro(Number(state.total))}
          </p>
          <p className="mt-4 text-muted-foreground">
            {state.estado === "pagada"
              ? "Tu pago está confirmado. Te hemos enviado un correo con tu código de compra y el justificante en PDF. Si no lo ves en unos minutos, revisa la carpeta de spam o promociones."
              : state.pago === "fallido"
                ? "El banco no ha completado el pago. Puedes intentarlo de nuevo; no necesitas crear otra inscripción."
                : "Estamos confirmando tu pago, recibirás el correo en unos minutos. Si ya has pagado, no vuelvas a pagar ni crees otra inscripción."}
          </p>
          {state.estado === "pendiente" && (
            <Button className="mt-8" onClick={pay} disabled={busy}>
              {busy ? "Conectando…" : "Reintentar pago"}
            </Button>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="mt-5 text-destructive">
          {error}
        </p>
      )}
      <Button asChild variant="outline" className="mt-8 ml-2">
        <Link to="/san-silvestre-sierra-norte">Volver al evento</Link>
      </Button>
    </main>
  );
}
